import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  basePath: "/",
  metadata: {
    // Override Zudoku's default "%s - Zudoku" tab-title template so no page
    // surfaces the framework name in tabs, search snippets, or link previews.
    title: "%s | Bevaya",
    defaultTitle: "Bevaya",
  },
  docs: {
    files: "pages/**/*.{md,mdx}", // Your markdown files
    publishMarkdown: true, // Generate .md files
    llms: {
      llmsTxt: true, // Generate llms.txt
      llmsTxtFull: true, // Generate llms-full.txt
      includeProtected: false, // Exclude protected routes
    },
  },
  site: {
    title: "Bevaya Platform Documentation",
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Bevaya Logo",
      width: "80px",
    },
    showPoweredBy: false,
  },
  navigation: [
    {
      type: "category",
      label: "Welcome",
      icon: "house",
      items: [
        "/welcome/overview",
        "/welcome/platform-concepts",
      ],
    },
    {
      type: "category",
      label: "Use Cases",
      icon: "layers",
      items: [
        "/use-cases/overview",
        "/use-cases/submission-intake",
        "/use-cases/claims-to-policy-comparison",
        "/use-cases/claims-indexing",
        "/use-cases/claims-file-summarization",
      ],
    },
    {
      type: "category",
      label: "Onboarding & User Management",
      icon: "users",
      items: [
        "/onboarding/platform-hierarchy",
        "/onboarding/users-access",
      ],
    },
    {
      type: "category",
      label: "Build AI Agents",
      icon: "bot",
      items: [
        "/build-ai-agents/overview",
        "/build-ai-agents/create-agent",
        "/build-ai-agents/triggers",
        "/build-ai-agents/node-catalog",
        "/build-ai-agents/app-integration-nodes",
        "/build-ai-agents/utility-nodes",
        "/build-ai-agents/action-nodes",
        "/build-ai-agents/control-nodes",
        "/build-ai-agents/insurgpt-nodes",
        "/build-ai-agents/environments-variables",
        "/build-ai-agents/test-debug-draft",
        "/build-ai-agents/drafts-publishing",
      ],
    },
    {
      type: "category",
      label: "Monitor & Review",
      icon: "activity",
      items: [
        "/monitor-review/overview",
        "/monitor-review/run-history",
        "/monitor-review/human-review",
        "/monitor-review/item-status-reporting",
      ],
    },
    {
      type: "category",
      label: "Reference",
      icon: "book-open",
      items: [
        "/reference/glossary",
        "/reference/core-entities",
        "/reference/ai-agents",
        "/reference/workflow-run-statuses",
        "/reference/work-item-statuses",
        "/reference/triggers",
        "/reference/node-catalog",
        "/reference/environments-variables",
        "/reference/item-statuses",
      ],
    },
    { type: "link", to: "/api", label: "API", icon: "code" },
  ],
  redirects: [{ from: "/", to: "/welcome/overview" }],
  apis: [
    {
      type: "file",
      input: "./api/platform.generated.json",
      path: "/api",
      options: { showInfoPage: true, expandAllTags: true },
    },
  ],
  theme: {
    customCss: `
      @import "tailwindcss";

      @custom-variant dark (&:is(.dark *));

      :root {
        /* --- 1. DIRECT SHADCN PRIMITIVES FOR TWEAKCN (RAW RGB EVALUATED) --- */
        --background: rgb(255 255 255);
        --foreground: rgb(10 10 10);
        --card: rgb(255 255 255);
        --card-foreground: rgb(10 10 10);
        --popover: rgb(255 255 255);
        --popover-foreground: rgb(10 10 10);

        --primary: rgb(121 40 230);             /* brand-600 */
        --primary-foreground: rgb(255 255 255);

        --secondary: rgb(250 250 250);          /* Evaluated from neutral-50 */
        --secondary-foreground: rgb(64 64 64);   /* Evaluated from neutral-700 */

        --muted: rgb(245 245 245);              /* Evaluated from neutral-100 */
        --muted-foreground: rgb(115 115 115);    /* Evaluated from neutral-500 */

        --accent: rgb(240 240 240);             /* Evaluated from neutral-100 hover state */
        --accent-foreground: rgb(10 10 10);

        --destructive: rgb(220 38 38);          /* Evaluated from red-600 */
        --destructive-foreground: rgb(255 255 255);

        --border: rgb(229 229 229);             /* Evaluated from neutral-200 */
        --input: rgb(214 214 214);              /* Evaluated from neutral-300 */
        --ring: rgb(139 55 255);                /* brand-500 */

        --radius: 0.375rem;

        /* --- 2. YOUR ORIGINAL BRAND DICTIONARY CONFIG (PRESERVED FOR REUSE) --- */
        --color-transparent: rgb(0 0 0 / 0);
        --color-white: rgb(255 255 255);
        --color-black: rgb(0 0 0);

        /* Bevaya brand palette */
        --color-brand-50: rgb(241 240 255);
        --color-brand-100: rgb(227 225 254);
        --color-brand-200: rgb(195 190 253);
        --color-brand-300: rgb(162 156 253);
        --color-brand-400: rgb(217 134 230);
        --color-brand-500: rgb(139 55 255);
        --color-brand-600: rgb(121 40 230);
        --color-brand-700: rgb(98 48 196);
        --color-brand-800: rgb(59 50 163);
        --color-brand-900: rgb(46 40 121);
        --color-brand-950: rgb(28 24 74);

        /* Base Neutrals (explicitly evaluated for compatibility) */
        --color-neutral-50: rgb(250 250 250);
        --color-neutral-100: rgb(245 245 245);
        --color-neutral-200: rgb(229 229 229);
        --color-neutral-300: rgb(214 214 214);
        --color-neutral-400: rgb(163 163 163);
        --color-neutral-500: rgb(115 115 115);
        --color-neutral-600: rgb(82 82 82);
        --color-neutral-700: rgb(64 64 64);
        --color-neutral-800: rgb(38 38 38);
        --color-neutral-900: rgb(23 23 23);
        --color-neutral-950: rgb(10 10 10);

        --color-red-600: rgb(220 38 38);
      }

      .dark {
        /* --- 3. DARK MODE DIRECT SHADCN PRIMITIVES (RAW RGB EVALUATED) --- */
        --background: rgb(10 10 10);            /* Evaluated from neutral-950 */
        --foreground: rgb(250 250 250);         /* Evaluated from neutral-50 */
        --card: rgb(23 23 23);                  /* Evaluated from neutral-900 */
        --card-foreground: rgb(250 250 250);
        --popover: rgb(23 23 23);               /* Evaluated from neutral-900 */
        --popover-foreground: rgb(250 250 250);

        --primary: rgb(250 250 250);            /* Evaluated from neutral-50 */
        --primary-foreground: rgb(10 10 10);

        --secondary: rgb(23 23 23);             /* Evaluated from neutral-900 */
        --secondary-foreground: rgb(214 214 214);/* Evaluated from neutral-300 */

        --muted: rgb(38 38 38);                 /* Evaluated from neutral-800 */
        --muted-foreground: rgb(163 163 163);   /* Evaluated from neutral-400 */

        --accent: rgb(38 38 38);                /* Evaluated from neutral-800 hover state */
        --accent-foreground: rgb(250 250 250);

        --destructive: rgb(220 38 38);          /* Evaluated from red-600 */
        --destructive-foreground: rgb(250 250 250);

        --border: rgb(38 38 38);                /* Evaluated from neutral-800 */
        --input: rgb(64 64 64);                 /* Evaluated from neutral-700 */
        --ring: rgb(139 55 255);                /* brand-500 */
      }

      /* --- 4. TAILWIND V4 COMPILER ENGINE BRIDGE --- */
      @theme inline {
        --color-background: var(--background);
        --color-foreground: var(--foreground);
        --color-card: var(--card);
        --color-card-foreground: var(--card-foreground);
        --color-popover: var(--popover);
        --color-popover-foreground: var(--popover-foreground);
        --color-primary: var(--primary);
        --color-primary-foreground: var(--primary-foreground);
        --color-secondary: var(--secondary);
        --color-secondary-foreground: var(--secondary-foreground);
        --color-muted: var(--muted);
        --color-muted-foreground: var(--muted-foreground);
        --color-accent: var(--accent);
        --color-accent-foreground: var(--accent-foreground);
        --color-destructive: var(--destructive);
        --color-destructive-foreground: var(--destructive-foreground);
        --color-border: var(--border);
        --color-input: var(--input);
        --color-ring: var(--ring);

        /* Font Configuration Cross-Mapping */
        --font-sans: var(--font-inter, "Inter"), -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
        --font-mono: ui-monospace, "Roboto Mono", SFMono-Regular, Menlo, Monaco, Consolas, monospace;

        /* Custom Radius In-line Generation Steps */
        --radius-none: 0px;
        --radius-xs: 0.125rem;
        --radius-sm: calc(var(--radius) - 2px);
        --radius-md: var(--radius);
        --radius-lg: calc(var(--radius) + 2px);
        --radius-xl: calc(var(--radius) + 4px);
        --radius-2xl: 1rem;
        --radius-3xl: 1.5rem;
        --radius-full: 9999px;

        /* Typographic Layout Engine Preserved */
        --text-xs: calc(var(--spacing) * 3);
        --text-xs--line-height: calc(var(--spacing) * 4.5);
        --text-sm: calc(var(--spacing) * 3.5);
        --text-sm--line-height: calc(var(--spacing) * 5);
        --text-md: calc(var(--spacing) * 4);
        --text-md--line-height: calc(var(--spacing) * 6);
        --text-lg: calc(var(--spacing) * 4.5);
        --text-lg--line-height: calc(var(--spacing) * 7);
        --text-xl: calc(var(--spacing) * 5);
        --text-xl--line-height: calc(var(--spacing) * 7.5);
        --text-display-xs: calc(var(--spacing) * 6);
        --text-display-xs--line-height: calc(var(--spacing) * 8);
        --text-display-sm: calc(var(--spacing) * 7.5);
        --text-display-sm--line-height: calc(var(--spacing) * 9.5);
        --text-display-md: calc(var(--spacing) * 9);
        --text-display-md--line-height: calc(var(--spacing) * 11);
        --text-display-md--letter-spacing: -0.72px;
        --text-display-lg: calc(var(--spacing) * 12);
        --text-display-lg--line-height: calc(var(--spacing) * 15);
        --text-display-lg--letter-spacing: -0.96px;
        --text-display-xl: calc(var(--spacing) * 15);
        --text-display-xl--line-height: calc(var(--spacing) * 18);
        --text-display-xl--letter-spacing: -1.2px;
        --text-display-2xl: calc(var(--spacing) * 18);
        --text-display-2xl--line-height: calc(var(--spacing) * 22.5);
        --text-display-2xl--letter-spacing: -1.44px;

        /* Extra Brand Elements */
        --max-width-container: max(1280px, 75vw);
        --breakpoint-xxs: 320px;
        --breakpoint-xs: 600px;

        /* Shadows Primitives */
        --shadow-xs: 0px 1px 2px rgba(0, 0, 0, 0.05);
        --shadow-sm: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1);
        --shadow-md: 0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.06);
        --shadow-lg: 0px 12px 16px -4px rgba(0, 0, 0, 0.08), 0px 4px 6px -2px rgba(0, 0, 0, 0.03), 0px 2px 2px -1px rgba(0, 0, 0, 0.04);
        --shadow-xl: 0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03), 0px 3px 3px -1.5px rgba(0, 0, 0, 0.04);
        --shadow-2xl: 0px 24px 48px -12px rgba(0, 0, 0, 0.18), 0px 4px 4px -2px rgba(0, 0, 0, 0.04);
        --shadow-3xl: 0px 32px 64px -12px rgba(0, 0, 0, 0.14), 0px 5px 5px -2.5px rgba(0, 0, 0, 0.04);
        --shadow-skeuomorphic: 0px 0px 0px 1px rgba(0, 0, 0, 0.18) inset, 0px -2px 0px 0px rgba(0, 0, 0, 0.05) inset;
        --shadow-xs-skeuomorphic: var(--shadow-skeuomorphic), var(--shadow-xs);

        /* Animation Configurations */
        --animate-marquee: marquee 60s linear infinite;
        --animate-caret-blink: caret-blink 1s infinite;

        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        @keyframes caret-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      }

      /* --- 5. DOM ELEMENT LAYER STYLING --- */
      @layer base {
        * {
          @apply border-border outline-ring/50;
        }
        body {
          @apply bg-background text-foreground;
        }
      }
    `,
  },
};

export default config;
